import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CiSearch } from "react-icons/ci";
import { FiPlus } from "react-icons/fi";
import { TailSpin } from "react-loader-spinner";

import { DataTable } from "../../components/data-table";
import {
  SURVEYS_TABLE_LIMIT,
  useSurveysMetricsQuery,
  useSurveysQuery,
} from "../../lib/api";
import SurveyPageShell from "./SurveyPageShell";
import SurveyStats from "./SurveyStats";
import {
  SURVEY_STATUS_TABS,
  statusFilterToApi,
  surveyErrorMessage,
  type SurveyStatusFilter,
} from "./surveyTypes";
import {
  buildSurveyTableRows,
  createSurveyTableColumns,
  type SurveyTableRow,
} from "./surveysTable";

const SurveysOverview: React.FC = () => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<SurveyStatusFilter>("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const { data: metrics, isPending: metricsPending } = useSurveysMetricsQuery();

  const { data, isPending, isError, error, refetch, isFetching } =
    useSurveysQuery({
      page,
      limit: SURVEYS_TABLE_LIMIT,
      search,
      status: statusFilterToApi(statusFilter),
    });

  const surveys = useMemo(() => data?.surveys ?? [], [data]);
  const pagination = data?.pagination;
  const totalPages = Math.max(1, pagination?.pages ?? 1);
  const totalCount = pagination?.total ?? surveys.length;

  const tableRows = useMemo(() => buildSurveyTableRows(surveys), [surveys]);

  const openDetail = useCallback(
    (row: SurveyTableRow) => navigate(`/surveys/${row.id}`),
    [navigate]
  );

  const columns = useMemo(
    () => createSurveyTableColumns({ onView: openDetail }),
    [openDetail]
  );

  return (
    <SurveyPageShell>
      <header className="mb-6">
        <h1 className="font-sora text-xl font-semibold tracking-tight text-white">
          Surveys
        </h1>
        <p className="mt-1 max-w-xl text-[13px] leading-relaxed text-[#869AA9]">
          Build surveys, choose where they appear in the app, and read what users
          tell you.
        </p>
      </header>

      <div className="space-y-6">
        <SurveyStats metrics={metrics} isLoading={metricsPending} />

        <section className="rounded-2xl border border-[#2C363E] bg-[#161E25]/40 p-4 sm:p-5">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="font-sora text-[16px] font-semibold text-white">
                All Surveys
              </h2>
              <p className="mt-1 text-[13px] text-[#869AA9]">
                Click a survey to see its results and responses.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/surveys/new")}
              className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-xl bg-secondary_button px-4 text-[13px] font-semibold text-white shadow-[0_8px_24px_rgba(11,132,203,0.35)] transition hover:brightness-110"
            >
              <FiPlus className="text-base" />
              Create Survey
            </button>
          </div>

          <div className="mb-3 flex h-11 items-center gap-2 rounded-xl border border-[#2C363E] bg-[#13181B] px-3">
            <CiSearch className="shrink-0 text-[22px] text-[#869AA9]" />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search surveys by title..."
              className="w-full bg-transparent text-[13px] text-[#E7EBEE] outline-none placeholder:text-[#5C6B76]"
            />
          </div>

          <div
            className="mb-4 flex flex-wrap gap-2"
            role="tablist"
            aria-label="Status filter"
          >
            {SURVEY_STATUS_TABS.map((tab) => {
              const active = statusFilter === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setStatusFilter(tab.id)}
                  className={`rounded-full border px-3.5 py-1.5 text-[12px] font-semibold transition ${
                    active
                      ? "border-secondary_button bg-secondary_button text-white"
                      : "border-[#2C363E] bg-[#1C2227] text-[#869AA9] hover:border-[#3A4852] hover:text-[#C6D0D7]"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          {isFetching && !isPending && (
            <p className="mb-2 text-[12px] text-[#869AA9]">Updating list…</p>
          )}

          {isError && (
            <div className="mb-4 flex flex-col gap-2 rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-3 text-[13px] text-red-100/95 sm:flex-row sm:items-center sm:justify-between">
              <span>{surveyErrorMessage(error, "Could not load surveys.")}</span>
              <button
                type="button"
                onClick={() => refetch()}
                className="shrink-0 rounded-lg border border-red-500/40 bg-red-500/20 px-3 py-1.5 text-[12px] font-semibold text-red-200"
              >
                Retry
              </button>
            </div>
          )}

          {isPending && !isError && (
            <div className="flex justify-center rounded-xl border border-[#2C363E] bg-[#161E25]/40 py-16">
              <TailSpin color="#0B75C2" height={36} width={36} />
            </div>
          )}

          {!isPending && !isError && (
            <DataTable<SurveyTableRow>
              columns={columns}
              data={tableRows}
              currentPage={page}
              setCurrentPage={setPage}
              totalPage={totalPages}
              count={totalCount}
              limit={SURVEYS_TABLE_LIMIT}
              onRowClick={openDetail}
              emptyContent="No surveys match this filter."
              className="overflow-hidden rounded-xl border border-[#2C363E]"
              fixedBodyHeight="min(520px, 50vh)"
            />
          )}
        </section>
      </div>
    </SurveyPageShell>
  );
};

export default SurveysOverview;
